import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import Illustration, {
  IllustrationKeyType,
} from 'shared/components/vxl/Illustration/Illustration';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isAndroid, isWeb } from 'shared/constants/platform';
import {
  AnalyticsProps,
  useViewDidFocusScreenAnalyticsEvent,
} from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useShouldShowDesktopNavigation } from 'shared/hooks/useShouldShowDesktopNavigation';
import theme from 'shared/styles/theme';
import { logError } from 'shared/utils/sentry/sentry';

/** Stable testID prefix on every PageDownErrorMessage root — lets e2e distinguish a service-down error screen from a broken locator. */
export const PAGE_DOWN_ERROR_MESSAGE_TEST_ID_PREFIX =
  'service-down-error-screen';

export interface PageDownErrorMessageProps extends Omit<AnalyticsProps, 'key'> {
  illustrationKey?: IllustrationKeyType;
  /** error title in bold */
  title?: string;
  /** error message and possible recovery options */
  message?: string;
  /** small text at the bottom */
  footerText?: string;
  primaryButtonText: string;
  primaryOnPress: () => void;
  primaryButtonIsLoading?: boolean;
  primaryButtonIsDisabled?: boolean;
  secondaryButtonText?: string;
  secondaryOnPress?: () => void;
  secondaryButtonIsLoading?: boolean;
  secondaryButtonIsDisabled?: boolean;
  error: unknown;
  screenAnalyticsEventKey?: string | undefined;
  hasPageHeader?: boolean;
  testID?: string;
}

const PageDownErrorMessage = ({
  illustrationKey,
  title,
  message,
  footerText,
  primaryButtonText,
  primaryOnPress,
  primaryButtonIsLoading = false,
  primaryButtonIsDisabled = false,
  secondaryButtonText,
  secondaryOnPress,
  secondaryButtonIsLoading = false,
  secondaryButtonIsDisabled = false,
  error,
  screenAnalyticsEventKey,
  customAttributes = null,
  hasPageHeader = false,
  testID = PAGE_DOWN_ERROR_MESSAGE_TEST_ID_PREFIX,
}: PageDownErrorMessageProps) => {
  const shouldShowDesktopNavigation = useShouldShowDesktopNavigation();
  useViewDidFocusScreenAnalyticsEvent(
    screenAnalyticsEventKey
      ? {
          key: screenAnalyticsEventKey,
          customAttributes,
        }
      : null,
  );

  useEffect(() => {
    logError(error);
  }, [error]);

  return (
    <View style={styles.container} testID={testID}>
      {illustrationKey ? (
        <View
          style={[
            styles.illustrationWrapper,
            hasPageHeader && styles.illustrationWrapperWithHeader,
            shouldShowDesktopNavigation && styles.illustrationWrapperDesktop,
          ]}
        >
          <Illustration illustration={illustrationKey} />
        </View>
      ) : null}
      <View style={styles.textContainer}>
        {title ? (
          <TitleMdText
            style={styles.title}
            numberOfLines={2}
            lineBreakMode="tail"
            ellipsizeMode="tail"
          >
            {title}
          </TitleMdText>
        ) : null}
        {message ? (
          <BodyBaseText style={styles.message}>{message}</BodyBaseText>
        ) : null}
      </View>
      <Button
        label={primaryButtonText}
        onPress={primaryOnPress}
        isLoading={primaryButtonIsLoading}
        isDisabled={primaryButtonIsDisabled}
      />
      {secondaryButtonText && secondaryOnPress ? (
        <Button
          label={secondaryButtonText}
          type="SECONDARY"
          onPress={secondaryOnPress}
          isLoading={secondaryButtonIsLoading}
          isDisabled={secondaryButtonIsDisabled}
        />
      ) : null}
      {footerText ? (
        <BodyXSText
          style={styles.footer}
          numberOfLines={1}
          lineBreakMode="tail"
          ellipsizeMode="tail"
        >
          {footerText}
        </BodyXSText>
      ) : null}
    </View>
  );
};

const mobileIllustrationMarginTop = isAndroid ? 121 : 159;

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'flex-start',
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    rowGap: theme.spacing.m,
    width: '100%',
  },
  footer: {
    color: theme.color.text.secondary,
    textAlign: 'center',
  },
  illustrationWrapper: {
    height: 126,
    marginTop: isWeb ? 84 : mobileIllustrationMarginTop, // based on figma, no equivalent theme value
    width: 126,
  },
  illustrationWrapperDesktop: {
    marginTop: 0,
  },
  illustrationWrapperWithHeader: {
    marginTop: 47, // based on figma, no equivalent theme value
  },
  message: {
    color: theme.color.text.primary,
    textAlign: 'center',
  },
  textContainer: {
    gap: theme.spacing.s,
  },
  title: {
    color: theme.color.text.primary,
    textAlign: 'center',
  },
});

export default PageDownErrorMessage;
